import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { fetchSquadPlayers } from '@lib/api/gytr-portraits';
import type { SquadPitchMember } from '@components/manual-team-entry/squad-pitch';
import type { ReadinessTeamMember } from 'types/readiness';

/**
 * Builds the readiness squad into the GYTR portrait pitch's member shape so
 * the readiness pitches can reuse SquadPitch. Names, teams and prices come
 * straight from the funnel team (rendered immediately); portrait images are
 * fetched by UUID and overlaid once resolved — so there is never a loading
 * gate, just silhouettes that fill in.
 *
 * The fetch is cached with react-query keyed on the sorted UUID list; the
 * captain step warms it and later steps (transfer, verdict) hit the cache.
 */
export const useSquadPortraits = (
  team: ReadinessTeamMember[]
): { pitchTeam: SquadPitchMember[]; isLoading: boolean } => {
  const uuids = useMemo(
    () =>
      team
        .map((member) => member.player?.id)
        .filter((id): id is string => Boolean(id)),
    [team]
  );

  const sortedKey = useMemo(() => [...uuids].sort(), [uuids]);

  const { data: portraits, isInitialLoading } = useQuery({
    // Fetch from the same sorted list we key on, so the query inputs and the
    // cache key never diverge when the team order changes but the set doesn't.
    queryKey: ['readiness-squad-portraits', sortedKey],
    queryFn: () => fetchSquadPlayers(sortedKey),
    enabled: uuids.length > 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity
  });

  const pitchTeam = useMemo<SquadPitchMember[]>(
    () =>
      team.map((member) => {
        const player = member.player;
        // Keep the squad's own slot id (0–14) so SquadPitch places each
        // player in the right row regardless of array order.
        if (!player) return { playerId: member.playerId, player: null };
        return {
          playerId: member.playerId,
          player: {
            id: player.id,
            displayName: player.displayName,
            teamShortName: player.team.shortName,
            price: player.price,
            position: player.position,
            imageUrl: portraits?.get(player.id)?.imageUrl ?? null
          }
        };
      }),
    [team, portraits]
  );

  return { pitchTeam, isLoading: isInitialLoading };
};
